import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, Key, RefreshCcw, AlertCircle } from 'lucide-react';
import axios from 'axios';

const Notch = ({ corner }) => (
  <div className={`absolute w-2 h-2 md:w-3 md:h-3 border-orange-500 z-10 opacity-60
    ${corner === 'top-left' ? 'top-0 left-0 border-t-2 border-l-2' : ''}
    ${corner === 'bottom-right' ? 'bottom-0 right-0 border-b-2 border-r-2' : ''}
  `} />
);

const Settings = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [status, setStatus] = useState({ type: '', msg: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      return setStatus({ type: 'error', msg: 'Passwords do not match' });
    }

    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/change-password`, formData, { 
        headers: { authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setStatus({ type: 'success', msg: 'Security Credentials Updated' });
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.message || 'Update failed' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-10 font-sans flex flex-col justify-center">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl mx-auto"
      >
        <header className="mb-6 md:mb-8 px-2">
          <h1 className="text-xl md:text-2xl font-black text-slate-950 uppercase italic tracking-tighter flex items-center gap-2 md:gap-3">
            <Lock className="text-orange-500" size={20} /> 
            <span className="truncate">Admin Security</span>
          </h1>
          <p className="text-slate-500 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] mt-1">
            System Access Configuration
          </p>
        </header>

        <div className="bg-white p-5 md:p-8 rounded-xl md:rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <Notch corner="top-left" />
          <Notch corner="bottom-right" />

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 relative z-10">
            {/* Field: Current Password */}
            <div>
              <label className="block text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">Current Password</label>
              <div className="relative">
                <Key className="absolute left-3 top-3 text-slate-400" size={16} />
                <input 
                  type="password" name="currentPassword" value={formData.currentPassword} onChange={handleChange} required
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:border-orange-500 outline-none transition-all placeholder:text-slate-300"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Field: New Password */}
            <div>
              <label className="block text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">New Access Key</label>
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-3 text-slate-400" size={16} />
                <input 
                  type="password" name="newPassword" value={formData.newPassword} onChange={handleChange} required
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:border-orange-500 outline-none transition-all placeholder:text-slate-300"
                  placeholder="Min. 8 characters"
                />
              </div>
            </div>

            {/* Field: Confirm Password */}
            <div>
              <label className="block text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 md:mb-2 ml-1">Confirm New Key</label>
              <div className="relative">
                <RefreshCcw className="absolute left-3 top-3 text-slate-400" size={16} />
                <input 
                  type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-sm focus:border-orange-500 outline-none transition-all placeholder:text-slate-300"
                  placeholder="Re-enter password"
                />
              </div>
            </div>

            {status.msg && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`p-3 rounded-lg flex items-center gap-2 text-[10px] md:text-xs font-bold uppercase tracking-tight ${
                status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {status.type === 'success' ? <ShieldCheck size={14}/> : <AlertCircle size={14}/>}
                {status.msg}
              </motion.div>
            )}

            <button 
              type="submit"
              className="w-full bg-slate-950 text-white font-black uppercase italic tracking-widest py-3 md:py-4 rounded-xl hover:bg-orange-500 transition-colors shadow-lg active:scale-[0.98] text-xs md:text-sm"
            >
              Update Security Protocol
            </button>
          </form>
        </div>

        <div className="mt-6 text-center px-4">
            <p className="text-[8px] md:text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em] leading-relaxed">
                Protected by Marvel Sato Engineering <br className="md:hidden" /> Security Standards
            </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Settings;